<template>
  <div :class="$style.wrapper">
    <section v-if="brandName && !isLoading" :class="$style.brandHeader">
      <div :class="$style.avatar">
        <img v-if="brandImageUrl" :src="brandImageUrl" :alt="brandName" />
        <span v-else>{{ brandInitial }}</span>
      </div>
      <h1 :class="$style.brandTitle">{{ brandName }}</h1>
      <div :class="$style.brandCount">{{ totalProducts }} товаров</div>
    </section>
    <section v-else-if="isLoading" :class="$style.brandHeader">
      <div :class="['skeleton', $style.avatarSkeleton]"></div>
      <div :class="['skeleton', $style.titleSkeleton]"></div>
      <div :class="['skeleton', $style.countSkeleton]"></div>
    </section>
    <section v-else :class="$style.brandHeader">
      <div :class="$style.placeholder">Бренд не найден</div>
    </section>

    <div :class="$style.topBar">
      <div :class="$style.searchBox">
        <input
          v-model="q"
          @input="handleSearch"
          type="text"
          placeholder="Поиск по товарам"
        />
        <img src="@/assets/img/market/search.svg" alt="" />
      </div>

      <button
        :class="[$style.iconBtn, { [$style.iconBtnActive]: hasFilters }]"
        type="button"
        @click="filtersOpen = true"
      >
        <img src="@/assets/img/market/filters.svg" alt="" />
      </button>

      <button :class="$style.iconBtn" type="button" @click="openSort">
        <img src="@/assets/img/market/sort.svg" alt="" />
      </button>
    </div>

    <div v-if="isLoading" :class="$style.grid">
      <div
        v-for="n in 6"
        :key="`brand-page-skeleton-${n}`"
        :class="$style.cardSkeleton"
      >
        <div :class="[$style.skeletonImage, 'skeleton']"></div>
        <div :class="[$style.skeletonLine, 'skeleton']"></div>
        <div :class="[$style.skeletonLineShort, 'skeleton']"></div>
      </div>
    </div>
    <div v-else-if="error" :class="$style.errorMsg">{{ error }}</div>
    <div v-else>
      <div v-if="items.length" :class="$style.grid">
        <Card
          v-for="item in items"
          :key="item.model_id"
          :item="item.cardItem"
          @click="openModel(item.model_id)"
        />
        <div v-if="isLoadingMore" :class="$style.loadingMore">
          <div :class="$style.loadingSpinner"></div>
        </div>
      </div>
      <div v-else :class="$style.empty">Товары не найдены</div>
    </div>

    <FiltersModal
      ref="filtersModal"
      v-if="filtersOpen"
      :filters="filters"
      :use-global-back-handler="true"
      :show-brand-filters="false"
      @close="filtersOpen = false"
      @apply="applyFilters"
      @open-brands="openBrandsSection"
    />

    <div
      v-if="sortModal"
      :class="[$style.sortBg, { [$style.fadeOut]: sortClosing }]"
      @click="closeSort"
    >
      <div
        :class="[$style.sortModal, { [$style.slideDown]: sortClosing }]"
        @click.stop
      >
        <div :class="$style.sortHeader"></div>
        <div :class="$style.sortTitle">Сортировка</div>

        <div :class="$style.sortList">
          <div
            v-for="option in sortOptions"
            :key="option.value"
            :class="$style.sortItem"
            @click="applySort(option.value)"
          >
            <span>{{ option.label }}</span>
            <div
              :class="[
                $style.radio,
                { [$style.radioActive]: sort === option.value },
              ]"
            >
              <img
                v-if="sort === option.value"
                src="@/assets/img/market/check.svg"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from "@/components/Card.vue"
import FiltersModal from "@/components/FiltersModal.vue"

export default {
  name: "BrandView",
  components: { Card, FiltersModal },
  data() {
    return {
      brandName: "",
      brandImageUrl: "",
      totalProducts: 0,
      items: [],

      isLoading: true,
      isLoadingMore: false,
      hasMore: true,
      error: "",

      q: "",
      limit: 20,
      offset: 0,

      sort: "random",
      sortModal: false,
      sortClosing: false,
      sortOptions: [
        { label: "По цене ↑", value: "price_asc" },
        { label: "По цене ↓", value: "price_desc" },
        { label: "Сначала новые", value: "date_desc" },
        { label: "Сначала старые", value: "date_asc" },
        { label: "Популярные", value: "random" },
      ],

      filtersOpen: false,
      filters: {
        brand: [],
        color: null,
        size: null,
        size_type: null,
        custom: null,
      },

      searchTimer: null,
      filtersBackHandlerRef: null,
      sortBackHandlerRef: null,
    }
  },
  computed: {
    brandSlug() {
      return this.$route.params.brand
    },
    brandInitial() {
      return (
        this.brandName?.[0]?.toUpperCase() ||
        this.brandSlug?.[0]?.toUpperCase() ||
        "B"
      )
    },
    hasFilters() {
      return (
        this.filters.brand.length > 0 ||
        this.filters.color !== null ||
        this.filters.size !== null ||
        this.filters.custom !== null
      )
    },
  },
  watch: {
    brandSlug() {
      this.resetState()
      this.initPage()
    },
    filtersOpen(value) {
      if (value) {
        this.registerFiltersBackHandler()
      } else {
        this.unregisterFiltersBackHandler()
      }
    },
    sortModal(value) {
      if (value) {
        this.registerSortBackHandler()
        this.$store.commit("setSortModalOpen", true)
      } else {
        this.unregisterSortBackHandler()
        this.$store.commit("setSortModalOpen", false)
      }
    },
  },
  async mounted() {
    this.q = this.$route.query.q || ""
    this.filters.color = this.$route.query.color || null
    this.filters.size = this.$route.query.size || null
    this.filters.size_type = this.$route.query.size_type || null
    if (this.$route.query.brand) {
      this.filters.brand = this.$route.query.brand.split(",")
    }
    this.sort = this.$route.query.sort || "random"
    await this.initPage()
    window.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer)
    this.unregisterFiltersBackHandler()
    this.unregisterSortBackHandler()
    // Сбрасываем состояние открытия сортировки в store
    this.$store.commit("setSortModalOpen", false)
    window.removeEventListener("scroll", this.handleScroll)
  },
  methods: {
    async initPage() {
      await this.loadBrandData(true)
    },
    resetState() {
      this.brandName = ""
      this.brandImageUrl = ""
      this.totalProducts = 0
      this.items = []
      this.q = ""
      this.filters = {
        brand: [],
        color: null,
        size: null,
        size_type: null,
        custom: null,
      }
      this.sort = "random"
      this.error = ""
      this.offset = 0
      this.hasMore = true
    },
    buildPayload() {
      return {
        brand: this.brandSlug,
        q: this.q || undefined,
        color: this.filters.color || undefined,
        size: this.filters.size || undefined,
        size_type: this.filters.size_type || undefined,
        is_custom:
          this.filters.custom !== null ? this.filters.custom : undefined,
        sort: this.sort,
        limit: this.limit,
        offset: this.offset,
      }
    },
    async loadBrandData(reset = false) {
      if (!this.brandSlug) return

      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
      } else {
        if (!this.hasMore || this.isLoadingMore) return
        this.isLoadingMore = true
      }

      this.error = ""
      try {
        const response = await this.$store.dispatch(
          "getBrand",
          this.buildPayload()
        )
        const data = response?.data || response || {}

        if (reset) {
          this.brandName = data.brand || this.brandSlug
          this.brandImageUrl = data.image_url || ""
          this.totalProducts = data.total || data.items?.length || 0
        }

        const items = data.items || []
        const mappedItems = items.map((item) => ({
          model_id: item.model_id,
          cardItem: {
            model_id: item.model_id,
            title: item.title,
            images:
              Array.isArray(item.images) && item.images.length > 0
                ? item.images
                : Array.isArray(item.model?.images) &&
                  item.model.images.length > 0
                ? item.model.images
                : [],
            preview_image: item.preview_image,
            min_price: Number(item.min_price),
            total_count: item.total_count,
            is_new: item.is_new,
            is_trade: item.is_trade,
          },
        }))

        if (reset) {
          this.items = mappedItems
        } else {
          this.items = [...this.items, ...mappedItems]
        }

        // Проверяем, есть ли еще данные
        this.hasMore = items.length === this.limit
        if (this.hasMore) {
          this.offset += this.limit
        }
      } catch (err) {
        console.error(err)
        this.error = "Не удалось загрузить бренд"
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // Загружаем, когда осталось 200px до конца
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadBrandData(false)
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.loadBrandData(true)
      }, 400)
    },
    openSort() {
      this.sortClosing = false
      this.sortModal = true
      // Устанавливаем состояние открытия сортировки в store
      this.$store.commit("setSortModalOpen", true)
    },
    closeSort() {
      this.sortClosing = true
      // Сбрасываем состояние открытия сортировки в store
      this.$store.commit("setSortModalOpen", false)
      setTimeout(() => {
        this.sortModal = false
        this.sortClosing = false
      }, 250)
    },
    async applySort(value) {
      this.sort = value
      this.closeSort()
      await this.loadBrandData(true)
    },
    async applyFilters(newFilters) {
      this.filters = newFilters
      this.filtersOpen = false
      await this.loadBrandData(true)
    },
    openModel(id) {
      this.$router.push({
        name: "Model",
        params: { id },
      })
    },
    closeFiltersModal() {
      if (this.$refs.filtersModal) {
        this.$refs.filtersModal.startClose()
      } else {
        this.filtersOpen = false
      }
    },
    openBrandsSection() {
      this.closeFiltersModal()
      this.$router.push({
        name: "market",
        query: { tab: "brands" },
      })
    },
    registerFiltersBackHandler() {
      if (this.filtersBackHandlerRef || !window.__AppBackHandlerStack) return
      this.filtersBackHandlerRef = () => {
        this.closeFiltersModal()
        return true
      }
      window.__AppBackHandlerStack.push(this.filtersBackHandlerRef)
    },
    unregisterFiltersBackHandler() {
      if (!this.filtersBackHandlerRef) return
      window.__AppBackHandlerStack?.pop(this.filtersBackHandlerRef)
      this.filtersBackHandlerRef = null
    },
    registerSortBackHandler() {
      if (this.sortBackHandlerRef || !window.__AppBackHandlerStack) return
      this.sortBackHandlerRef = () => {
        this.closeSort()
        return true
      }
      window.__AppBackHandlerStack.push(this.sortBackHandlerRef)
    },
    unregisterSortBackHandler() {
      if (!this.sortBackHandlerRef) return
      window.__AppBackHandlerStack?.pop(this.sortBackHandlerRef)
      this.sortBackHandlerRef = null
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 1.25rem 1rem 6.5rem 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.brandHeader {
  display: grid;
  justify-items: center;
  margin-bottom: 2.125rem;
  text-align: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.avatar {
  width: 5.625rem;
  height: 5.625rem;
  background: transparent;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.brandTitle {
  font-size: 1.5rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.25rem;
}

.brandCount {
  font-size: 1rem;
  color: #7b7b81;
}

.avatarSkeleton,
.titleSkeleton,
.countSkeleton {
  max-width: 100%;
  box-sizing: border-box;
}

.avatarSkeleton {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
}

.titleSkeleton {
  width: 60%;
  height: 1.25rem;
}

.countSkeleton {
  width: 40%;
  height: 1rem;
}

.topBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.searchBox {
  flex: 1;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.875rem;
  }

  img {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.iconBtn {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  background: #fff;
  margin-left: 0.375rem;
}

.iconBtnActive {
  border-color: #000;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.cardSkeleton {
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid #b8b8bc5c;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.skeletonImage {
  width: 100%;
  max-width: 100%;
  height: 5.5rem;
  border-radius: 0.5rem;
  box-sizing: border-box;
}

.skeletonLine,
.skeletonLineShort {
  height: 0.75rem;
  border-radius: 0.375rem;
  box-sizing: border-box;
}

.skeletonLine {
  width: 100%;
  max-width: 100%;
}

.skeletonLineShort {
  width: 60%;
  max-width: 100%;
}

.placeholder,
.empty,
.errorMsg {
  text-align: center;
  color: #7b7b81;
}

.errorMsg {
  color: #d53a3a;
}

.sortBg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    backdrop-filter: blur(10px);
  }
  to {
    opacity: 0;
    backdrop-filter: blur(0);
  }
}

.fadeOut {
  animation: fadeOut 0.25s ease forwards;
}

.sortModal {
  width: 100%;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.5rem 1rem;
  animation: slideUp 0.25s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(40px);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideDownAnim {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(40px);
    opacity: 0;
  }
}

.slideDown {
  animation: slideDownAnim 0.25s ease forwards;
}

.sortHeader {
  width: 3.25rem;
  height: 0.375rem;
  background: #ddd;
  border-radius: 0.5rem;
  margin: 0 auto 0.875rem;
}

.sortTitle {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
}

.sortList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sortItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f7f7f7;
  border-radius: 0.5rem;
}

.radio {
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid #bbb;
}

.radioActive {
  background: #f07818;
  border-color: #f07818;
}

.radio img {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loadingMore {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
